import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { DiskSpaceCheck } from "@via/components/disk-space-check/disk-space-check.tsx";
import { GenericError } from "@via/components/error.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import { HeadingSize, PageHeading } from "@via/components/page-heading.tsx";
import RichTextInput from "@via/components/rich-text-input/rich-text-input.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import {
  RUN_ENVIRONMENT_SELECT_INPUT_ID,
  type EnvironmentPickerFilter,
} from "@via/pages/projects/runs/use-launch-refusal";
import { ERROR, USER_ROLES } from "@via/utils/constants.tsx";
import { type SelectOption } from "@via/utils/interfaces.tsx";
import { type GetRunDetailsResponseBody } from "common";
import { type FC, useEffect, useRef } from "react";
import { type UseFormReturn } from "react-hook-form";
import { Tooltip } from "react-tooltip";

interface SettingsProps {
  form: UseFormReturn;
  runSettingsData: GetRunDetailsResponseBody;
  isRunSettingsSuccess: boolean;
  isRunSettingsLoading: boolean;
  isRunSettingsError: boolean;
  readOnly: boolean;
  /**
   * 3G-3a spec 5.3: an unsaved environment switch the run page holds. While set and offered by the fetched
   * list, a run details refetch must not put the fetched environment back (D8), and hostname,
   * selectedRunEnvironment and launchDirectory follow the pending selection, not the fetched one (D13).
   */
  pendingRunEnvironmentId?: number | null;
  /** 3G-3a: when set, the selector opens with SSH environments only. Component state, never a form field (D11). */
  environmentPicker?: EnvironmentPickerFilter;
  /** A user pick in the selector, with whether it came through the picker. Never fired by the reset effect. */
  onEnvironmentChange?: (
    environment: { id: number; name: string },
    source: "picker" | "user"
  ) => void;
  /** The picker's menu closed; `picked` says whether a choice preceded the close. */
  onEnvironmentPickerClosed?: (outcome: { picked: boolean }) => void;
  /** The run's id, for a shared run's viewer, whose disk space check goes to the run itself. */
  runId?: number | null;
}

const Settings: FC<SettingsProps> = ({
  form,
  runSettingsData,
  isRunSettingsSuccess,
  isRunSettingsLoading,
  isRunSettingsError,
  readOnly,
  pendingRunEnvironmentId = null,
  environmentPicker = null,
  onEnvironmentChange,
  onEnvironmentPickerClosed,
  runId = null,
}) => {
  const { control, setValue, watch } = form;

  const runEnvironmentId = watch("runEnvironmentId");
  const selectedRunEnvironment = watch("selectedRunEnvironment");
  const launchDirectory = watch("launchDirectory");

  // Read through a ref inside the reset effect, on purpose: the effect's dependencies stay what they were, so
  // setting a pending selection never re-runs it (which would be a new moment at which an unsaved summary edit
  // is overwritten). The effect only needs the value current at refetch time.
  const pendingRef = useRef<number | null>(pendingRunEnvironmentId);
  pendingRef.current = pendingRunEnvironmentId;

  useEffect(() => {
    if (isRunSettingsSuccess) {
      const availableList =
        runSettingsData?.runEnvironment?.availableList ?? [];
      let fetchedId: string | number = "";
      if (availableList.length) {
        fetchedId = runSettingsData?.runEnvironment?.selectedId ?? "";
      }
      const pendingId = pendingRef.current;
      const pendingHeld =
        pendingId !== null && availableList.some(({ id }) => id === pendingId);

      setValue("summary", runSettingsData?.summary ?? "");
      if (pendingHeld) {
        // The unsaved switch wins over the refetch (D8). hostname and selectedRunEnvironment are derived
        // from runEnvironmentId by the effect below, so nothing here writes them from the fetched selection
        // (D13). The execution directory belongs to the submission the user is about to make, so it keeps its
        // local value until the fetched selection is the pending one.
        setValue("runEnvironmentId", pendingId);
        if (pendingId === fetchedId) {
          setValue("launchDirectory", runSettingsData?.launchDirectory ?? "");
        }
        return;
      }

      setValue("runEnvironmentId", fetchedId);
      setValue(
        "hostname",
        availableList.find(
          ({ id }) => id === runSettingsData?.runEnvironment?.selectedId
        )?.hostname ?? ""
      );
      setValue(
        "selectedRunEnvironment",
        availableList.find(
          ({ id }) => id === runSettingsData?.runEnvironment?.selectedId
        )
      );
      setValue("launchDirectory", runSettingsData?.launchDirectory ?? "");
    }
  }, [isRunSettingsSuccess, runSettingsData, setValue]);

  useEffect(() => {
    if (runEnvironmentId && isRunSettingsSuccess) {
      const selectedRunEnvironment =
        runSettingsData?.runEnvironment?.availableList?.find(
          ({ id }) => id.toString() === runEnvironmentId.toString()
        );
      if (selectedRunEnvironment) {
        setValue("hostname", selectedRunEnvironment.hostname);
        setValue("selectedRunEnvironment", selectedRunEnvironment);
      }
    }
  }, [isRunSettingsSuccess, runSettingsData, runEnvironmentId, setValue]);

  const refreshEnvironment = () => {
    setValue("selectedRunEnvironmentUpdated", true, { shouldDirty: true });
  };

  const pickerActive = environmentPicker?.transport === "ssh";
  // Whether a choice happened while the picker was open. react-select fires onChange and then onMenuClose in
  // the same event, and React batches the state update in between, so a ref is what tells the close apart.
  const pickerPickedRef = useRef(false);
  useEffect(() => {
    if (pickerActive) pickerPickedRef.current = false;
  }, [pickerActive]);

  // Whose access the disk space check uses. The owner and an admin check the form's environment and directory as the
  // owner, whose environment it is. Any other viewer checks the run itself: the server checks the run's SAVED
  // environment and directory as its owner, free space only, and the viewer sends no path or user. While the owner
  // is unknown the check waits: the request would otherwise fall back to an admin's leftover impersonation.
  const { user } = useAuth();
  const ownerId = runSettingsData?.owner?.id;
  const checksAsOwner =
    user?.userRole === USER_ROLES.ADMIN ||
    (null != ownerId && user?.id === ownerId);

  const availableList = runSettingsData?.runEnvironment?.availableList ?? [];
  const environmentOptions = availableList
    .filter(({ transport }) => !pickerActive || transport === "ssh")
    .map(({ id, name }) => ({ label: name, value: id }));

  const handleEnvironmentSelected = (
    selected: SelectOption | readonly SelectOption[] | null
  ): void => {
    const value = Array.isArray(selected)
      ? undefined
      : (selected as SelectOption | null)?.value;
    const environment = availableList.find(({ id }) => id === value);
    if (!environment) return;
    // react-select fires onChange on a re-pick of the selected option too, which is not a change.
    if (String(environment.id) === String(runEnvironmentId)) return;
    if (pickerActive) pickerPickedRef.current = true;
    onEnvironmentChange?.(
      { id: environment.id, name: environment.name },
      pickerActive ? "picker" : "user"
    );
  };

  const handleMenuClose = (): void => {
    if (pickerActive) {
      onEnvironmentPickerClosed?.({ picked: pickerPickedRef.current });
    }
  };

  return (
    <div className="mb-4 px-4 py-2 shadow-md ring-1 ring-gray-900/5 sm:mx-0 rounded-lg md:px-6 lg:px-6 md:py-3 lg:py-3 lg:col-span-2 lg:row-span-2 lg:row-end-2">
      <div className="flex items-center justify-between">
        <PageHeading title="Settings" size={HeadingSize.Small} />
      </div>
      {isRunSettingsLoading ? (
        <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
          <SkeletonLoader count={6} />
        </div>
      ) : isRunSettingsError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : isRunSettingsSuccess ? (
        <>
          <div className="flex flex-col py-2 text-sm leading-6 text-slate-600 md:py-3">
            <RichTextInput
              control={control}
              name="summary"
              label="Run Description"
              disabled={readOnly}
            />
          </div>
          <div className="flex flex-col py-2 text-sm leading-6 text-slate-600 md:py-3">
            <label className="block flex text-sm font-medium leading-6 text-gray-900">
              Run Environment
              {!readOnly && (
                <button type="button" onClick={refreshEnvironment}>
                  <ArrowPathIcon
                    id="refresh-environment"
                    className="stroke-2.5 ml-1 h-5 w-5 stroke-current text-indigo-600 duration-200 ease-in-out hover:text-indigo-500"
                    aria-hidden="true"
                  />
                </button>
              )}
            </label>
            <div className="mt-2">
              <SelectComponent
                control={control}
                name="runEnvironmentId"
                inputId={RUN_ENVIRONMENT_SELECT_INPUT_ID}
                placeholder="Choose an environment"
                options={environmentOptions}
                readOnly={readOnly}
                enableClear={false}
                menuIsOpen={pickerActive ? true : undefined}
                onChange={handleEnvironmentSelected}
                onMenuClose={handleMenuClose}
              />
            </div>
            <Tooltip
              anchorSelect="#refresh-environment"
              render={() => <>Refresh Environment</>}
            />
          </div>
          {!(
            selectedRunEnvironment?.hideWorkdir === "1" ||
            selectedRunEnvironment?.hideWorkdir === "on"
          ) && (
            <div className="flex flex-col -mt-2 py-2 text-sm leading-6 text-slate-600 md:py-3">
              <FormInputComponent
                control={control}
                name="launchDirectory"
                label="Execution Directory"
                placeholder="Enter Execution directory"
                showTooltipIcon
                tooltipText="Please enter the full path of the execution directory in your host. eg. /project/rna-seq/run1"
                disabled={readOnly}
              />
              {/* Before the run: how much room the filesystem under this directory has. Always shown, so a
                  directory cleared by an environment switch leaves the check visible with its reason. */}
              <DiskSpaceCheck
                testId="disk-space-launch-directory"
                runEnvironmentId={Number(runEnvironmentId) || null}
                asUserId={checksAsOwner ? ownerId : undefined}
                runId={checksAsOwner ? undefined : runId}
                path={launchDirectory}
                disabledReason={
                  null == ownerId
                    ? "Loading the run's owner."
                    : String(launchDirectory ?? "").trim()
                      ? undefined
                      : "Enter an execution directory to check its disk space."
                }
                accessNote={
                  checksAsOwner
                    ? undefined
                    : "Checked as the run's owner, on its saved environment and directory."
                }
              />
            </div>
          )}
        </>
      ) : (
        <></>
      )}
    </div>
  );
};

export default Settings;
